import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mock';

const Services = () => {
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1767420073545-fe0a1f78b022?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxzdXJ2ZXklMjB2ZXNzZWx8ZW58MHx8fHwxNzcxMjAyMTQwfDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">Our Services</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Comprehensive marine inspection and technical services tailored to meet 
              the diverse needs of the global maritime industry.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-maritime-primary mb-4">
              Complete Marine Survey Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From routine inspections to complex investigations, our experienced surveyors 
              deliver accurate and reliable services across all maritime sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = require('lucide-react')[service.icon];
              const isHovered = hoveredId === service.id;

              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-maritime-accent/30 overflow-hidden cursor-pointer"
                  onMouseEnter={() => setHoveredId(service.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className={`w-full h-full object-cover transition-all duration-700 ${
                        isHovered ? 'scale-110' : 'scale-100'
                      }`}
                    />
                    <div
                      className={`absolute inset-0 transition-colors duration-300 ${
                        isHovered ? 'bg-maritime-accent/50' : 'bg-maritime-primary/70'
                      }`}
                    ></div>
                    <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-lg">
                      <IconComponent className="w-6 h-6 text-maritime-accent" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-maritime-accent text-sm font-medium">
                        <span>Professional Survey Service</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Services Notice */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto bg-maritime-primary/5 border-2 border-maritime-accent/20">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-maritime-primary mb-3">
                  ...and much more
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Our comprehensive service portfolio extends beyond these offerings. 
                  Contact us to discuss your specific marine survey requirements and 
                  discover how we can assist your operations.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center text-maritime-accent hover:text-maritime-accent/80 font-medium transition-colors"
                >
                  Discuss Your Requirements
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-gradient-to-b from-maritime-primary/5 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-maritime-primary text-center mb-12">
              What's Included in Our Services
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-maritime-accent/10 hover:border-maritime-accent/30 transition-colors">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-maritime-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-maritime-primary mb-3">
                    Comprehensive Reporting
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Detailed, transparent reports with photographic evidence and 
                    clear recommendations for all survey findings.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-maritime-accent/10 hover:border-maritime-accent/30 transition-colors">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-maritime-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-maritime-primary mb-3">
                    Rapid Response
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Quick mobilization and efficient survey execution to minimize 
                    downtime and meet urgent requirements.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-maritime-accent/10 hover:border-maritime-accent/30 transition-colors">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-maritime-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-maritime-primary mb-3">
                    Compliance Assurance
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    All surveys conducted in accordance with international maritime 
                    standards and regulatory requirements.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-maritime-accent/10 hover:border-maritime-accent/30 transition-colors">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-maritime-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-maritime-primary mb-3">
                    Expert Team
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Certified marine surveyors with extensive experience across 
                    all vessel types and maritime operations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-maritime-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Need a Marine Survey?</h2>
            <p className="text-xl text-gray-200 mb-8">
              Contact our team to discuss your specific requirements and receive 
              a detailed quote for our professional services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="px-8 py-3 bg-maritime-accent hover:bg-maritime-accent/90 text-white rounded-lg font-medium transition-colors duration-300 w-full sm:w-auto">
                  Request a Quote
                </button>
              </a>
              <a href="tel:+64212314953">
                <button className="px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-maritime-primary rounded-lg font-medium transition-colors duration-300 w-full sm:w-auto">
                  Call Now
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
